import React, { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Search, X, Eraser, ChevronDown, ListFilter } from 'lucide-react';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { motion, AnimatePresence } from 'framer-motion';
import {
  selectAllBrands,
  selectAllFlavors,
  selectAllAttributes,
  selectGlobalDataLoading,
  selectGlobalDataInitialized,
  selectGlobalDataError,
} from '@/store/slices/globalDataSlice';
import {
  setSearchTerm,
  setBrandSize,
  toggleBrandId,
  toggleFlavorId,
  toggleAttributeId,
  setMinRating,
  setMaxRating,
  resetSodaSearchFilters,
  selectSodaSearchFilters,
  type SodaSearchBrandSizeType,
  toggleDisplayOption,
  type SodaDisplayOptionsState
} from '@/store/slices/sodaSearchFiltersSlice';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { MultiSelect } from '@/components/ui/multi-select';
import { GroupedMultiSelect } from '@/components/ui/grouped-multi-select';
import { Checkbox } from "@/components/ui/checkbox";

const SODA_BRAND_SIZE_OPTIONS: { value: SodaSearchBrandSizeType; label: string }[] = [
  { value: 'all', label: 'All Brands' },
  { value: 'craft', label: 'Craft Brands' },
  { value: 'mass', label: 'Mass Market' },
  { value: 'unknown', label: 'Unknown' },
];

interface FilterBadge {
  id: string;
  type: 'brand' | 'flavor' | 'attribute' | 'rating' | 'brandSize' | 'searchTerm';
  label: string;
  value: string | number | boolean | null;
}

// This will be our custom display options component that doesn't close when toggling options
const DisplayOptionsPopover: React.FC<{
  displayOptions: SodaDisplayOptionsState;
  onToggleOption: (option: keyof SodaDisplayOptionsState) => void;
}> = ({ displayOptions, onToggleOption }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-10">
          <ListFilter className="mr-2 h-4 w-4" />
          Display
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-56 p-2">
        <div className="flex flex-col space-y-2">
          <div className="text-sm font-medium pb-1">Toggle Card Elements</div>
          <div className="flex items-center space-x-2">
            <Checkbox 
              id="showFlavors" 
              checked={displayOptions.showFlavors} 
              onCheckedChange={() => onToggleOption('showFlavors')} 
            />
            <Label htmlFor="showFlavors" className="text-sm font-normal cursor-pointer">Flavors</Label>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox 
              id="showAttributes" 
              checked={displayOptions.showAttributes} 
              onCheckedChange={() => onToggleOption('showAttributes')} 
            />
            <Label htmlFor="showAttributes" className="text-sm font-normal cursor-pointer">Attributes</Label>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox 
              id="showStarRating" 
              checked={displayOptions.showStarRating} 
              onCheckedChange={() => onToggleOption('showStarRating')} 
            />
            <Label htmlFor="showStarRating" className="text-sm font-normal cursor-pointer">Star Rating</Label>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox 
              id="showReviewCount" 
              checked={displayOptions.showReviewCount} 
              onCheckedChange={() => onToggleOption('showReviewCount')} 
            />
            <Label htmlFor="showReviewCount" className="text-sm font-normal cursor-pointer">Review Count</Label>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

const SodaSearchFilterControls: React.FC = () => {
  const dispatch = useAppDispatch();
  const allFilters = useAppSelector(selectSodaSearchFilters);
  const { 
    searchTerm: reduxSearchTerm, 
    brandSize, 
    selectedBrandIds, 
    selectedFlavorIds, 
    selectedAttributeIds, 
    minRating, 
    maxRating,
    displayOptions
  } = allFilters;

  const [localSearchInput, setLocalSearchInput] = useState<string>(reduxSearchTerm);
  const [localMinRating, setLocalMinRating] = useState<string>(minRating?.toString() || '');
  const [localMaxRating, setLocalMaxRating] = useState<string>(maxRating?.toString() || '');
  const [activeFilterBadges, setActiveFilterBadges] = useState<FilterBadge[]>([]);

  // Global data selectors
  const globalDataLoading = useAppSelector(selectGlobalDataLoading);
  const globalDataInitialized = useAppSelector(selectGlobalDataInitialized);
  const globalDataError = useAppSelector(selectGlobalDataError);

  useEffect(() => {
    setLocalSearchInput(reduxSearchTerm);
  }, [reduxSearchTerm]);

  useEffect(() => setLocalMinRating(minRating?.toString() || ''), [minRating]);
  useEffect(() => setLocalMaxRating(maxRating?.toString() || ''), [maxRating]);

  const allBrands = useAppSelector(selectAllBrands);
  const allFlavors = useAppSelector(selectAllFlavors);
  const allAttributes = useAppSelector(selectAllAttributes);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      dispatch(setSearchTerm(localSearchInput)); 
    }
  };

  const handleClearSearch = () => {
    setLocalSearchInput('');
    dispatch(setSearchTerm(''));
  };
  
  const handleResetAllFilters = () => {
    dispatch(resetSodaSearchFilters());
    setLocalSearchInput('');
    setLocalMinRating('');
    setLocalMaxRating('');
  };

  const handleMinRatingChange = (value: string) => {
    setLocalMinRating(value);
    const num = parseFloat(value);
    dispatch(setMinRating(isNaN(num) ? null : num));
  };
  const handleMaxRatingChange = (value: string) => {
    setLocalMaxRating(value);
    const num = parseFloat(value);
    dispatch(setMaxRating(isNaN(num) ? null : num));
  };
  
  useEffect(() => {
    const badgesByType: Record<FilterBadge['type'], FilterBadge[]> = {
      'searchTerm': [], 'brandSize': [], 'brand': [], 'flavor': [], 'attribute': [], 'rating': []
    };

    if (reduxSearchTerm) {
        badgesByType.searchTerm.push({id: 'searchTerm', type: 'searchTerm', label: `Search: "${reduxSearchTerm}"`, value: reduxSearchTerm });
    }
    if (brandSize !== 'all') {
      const option = SODA_BRAND_SIZE_OPTIONS.find(opt => opt.value === brandSize);
      if (option) badgesByType.brandSize.push({ id: `brandSize-${brandSize}`, type: 'brandSize', label: option.label, value: brandSize });
    }
    selectedBrandIds.forEach(id => { const item = allBrands.find(i => i.id === id); if (item) badgesByType.brand.push({ id: `brand-${id}`, type: 'brand', label: item.name, value: id }); });
    selectedFlavorIds.forEach(id => { const item = allFlavors.find(i => i.id === id); if (item) badgesByType.flavor.push({ id: `flavor-${id}`, type: 'flavor', label: item.name, value: id }); });
    selectedAttributeIds.forEach(id => { const item = allAttributes.find(i => i.id === id); if (item) badgesByType.attribute.push({ id: `attr-${id}`, type: 'attribute', label: item.name, value: id }); });
    const ratingLabel = maxRating !== null || minRating !== null ? ((minRating !== null ? `Min ≥ ${minRating.toFixed(1)}` : '') + (minRating !== null && maxRating !== null ? ' ' : '') + (maxRating !== null ? ` Max ≤ ${maxRating.toFixed(1)}` : '')).trim() : null;
    if (ratingLabel) badgesByType.rating.push({ id: 'rating', type: 'rating', label: ratingLabel, value: `${minRating ?? ''}-${maxRating ?? ''}` });
    
    (Object.keys(badgesByType) as Array<FilterBadge['type']>).forEach(type => {
      if (type !== 'rating' && type !== 'brandSize' && type !== 'searchTerm') {
        badgesByType[type].sort((a, b) => a.label.localeCompare(b.label));
      }
    });
    
    setActiveFilterBadges([
      ...badgesByType.searchTerm, ...badgesByType.brandSize, ...badgesByType.rating, 
      ...badgesByType.brand, ...badgesByType.flavor, ...badgesByType.attribute
    ]);
  }, [reduxSearchTerm, brandSize, selectedBrandIds, selectedFlavorIds, selectedAttributeIds, minRating, maxRating, allBrands, allFlavors, allAttributes]);

  const handleRemoveBadge = (badge: FilterBadge) => {
    switch (badge.type) {
      case 'searchTerm': dispatch(setSearchTerm('')); break;
      case 'brandSize': dispatch(setBrandSize('all')); break;
      case 'brand': dispatch(toggleBrandId(badge.value as string)); break;
      case 'flavor': dispatch(toggleFlavorId(badge.value as string)); break;
      case 'attribute': dispatch(toggleAttributeId(badge.value as string)); break;
      case 'rating': dispatch(setMinRating(null)); dispatch(setMaxRating(null)); break;
    }
  };
  const handleBrandToggle = (id: string) => dispatch(toggleBrandId(id));
  const handleFlavorToggle = (id: string) => dispatch(toggleFlavorId(id));
  const handleAttributeToggle = (id: string) => dispatch(toggleAttributeId(id));

  const isFilterDataLoading = globalDataLoading === 'pending' && !globalDataInitialized;
  const filterDataError = globalDataError;

  const handleToggleDisplayOption = (option: keyof SodaDisplayOptionsState): void => {
    dispatch(toggleDisplayOption(option));
  };

  return (
    <div className="max-h-full flex flex-col bg-card text-card-foreground rounded-lg border shadow-sm overflow-hidden">
      <div className="p-4 flex flex-row items-center justify-between border-b">
        <h3 className="text-lg font-semibold">Filter Sodas</h3>
        <DisplayOptionsPopover 
          displayOptions={displayOptions} 
          onToggleOption={handleToggleDisplayOption} 
        />
      </div>
      <div className="flex-grow overflow-y-auto space-y-3 p-4">
        {/* Freetext Search */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <div className="relative flex items-center gap-2">
            <div className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground">
                <Search size={16}/>
            </div>
            <Input
              id="searchTermInput"
              type="text"
              placeholder="Search sodas..."
              value={localSearchInput} 
              onChange={(e) => setLocalSearchInput(e.target.value)} 
              onKeyDown={handleKeyDown} 
              className="w-full pl-9 pr-8" 
            />
            {localSearchInput && (
              <Button
                variant="ghost" size="icon"
                className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                onClick={handleClearSearch}
                aria-label="Clear search"
              >
                <X size={16} />
              </Button>
            )}
          </div>
        </motion.div>

        {/* Brand Size Filter */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
        >
          <Popover>
            <PopoverTrigger asChild>
              <div className="flex h-10 w-full cursor-pointer items-center justify-between rounded-md border border-input bg-transparent px-3 py-2 text-sm ring-offset-background focus-within:outline-none focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
                <span className={brandSize === 'all' ? 'text-muted-foreground' : 'text-card-foreground'}>
                  {brandSize !== 'all'
                    ? SODA_BRAND_SIZE_OPTIONS.find(opt => opt.value === brandSize)?.label
                    : 'Select Brand Size'}
                </span>
                <ChevronDown size={16} className="ml-2 shrink-0 opacity-50" />
              </div>
            </PopoverTrigger>
            <PopoverContent className="w-[--radix-popover-trigger-width] p-2">
              <RadioGroup
                value={brandSize}
                onValueChange={(value: string) => dispatch(setBrandSize(value as SodaSearchBrandSizeType))}
                className="gap-2"
              >
                {SODA_BRAND_SIZE_OPTIONS.map((option) => (
                  <div key={option.value} className="flex items-center space-x-2">
                    <RadioGroupItem
                      id={`brandsize-${option.value}`}
                      value={option.value}
                    />
                    <Label
                      htmlFor={`brandsize-${option.value}`}
                      className="text-sm font-normal cursor-pointer flex-grow"
                    >
                      {option.label}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </PopoverContent>
          </Popover>
        </motion.div>

        {/* Rating Filter */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.2 }}
        >
          <div className="flex gap-2">
            <Input type="number" placeholder="Min Rating" value={localMinRating} onChange={(e) => handleMinRatingChange(e.target.value)} min="0" max="5" step="0.1" className="w-full" />
            <Input type="number" placeholder="Max Rating" value={localMaxRating} onChange={(e) => handleMaxRatingChange(e.target.value)} min="0" max="5" step="0.1" className="w-full" />
          </div>
        </motion.div>

        {/* Brand Filter */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.3 }}
        >
          <MultiSelect
            items={allBrands || []}
            selectedIds={selectedBrandIds}
            onToggleItem={handleBrandToggle}
            placeholder="Select Brands"
            emptyMessage={isFilterDataLoading ? "Loading brands..." : (filterDataError ? "Error loading brands" : "No brands found")}
            searchPlaceholder="Search brands..."
            disabled={isFilterDataLoading || !!filterDataError || (allBrands || []).length === 0}
          />
          {filterDataError && <p className="text-xs text-destructive mt-1">Failed to load brands: {filterDataError}</p>}
        </motion.div>

        {/* Flavor Filter */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.4 }}
        >
          <GroupedMultiSelect
            items={allFlavors}
            selectedIds={selectedFlavorIds}
            onToggleItem={handleFlavorToggle}
            placeholder="Select Flavors"
            emptyMessage={isFilterDataLoading ? "Loading flavors..." : (filterDataError ? "Error loading flavors" : "No flavors found")}
            searchPlaceholder="Search flavors..."
            disabled={isFilterDataLoading || !!filterDataError || (allFlavors || []).length === 0}
            hideGroupTitles={true} 
          />
          {filterDataError && <p className="text-xs text-destructive mt-1">Failed to load flavors: {filterDataError}</p>}
        </motion.div>

        {/* Attribute Filter */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.5 }}
        >
          <GroupedMultiSelect
            items={allAttributes}
            selectedIds={selectedAttributeIds}
            onToggleItem={handleAttributeToggle}
            placeholder="Select Attributes"
            emptyMessage={isFilterDataLoading ? "Loading attributes..." : (filterDataError ? "Error loading attributes" : "No attributes found")}
            searchPlaceholder="Search attributes..."
            disabled={isFilterDataLoading || !!filterDataError || (allAttributes || []).length === 0}
            hideGroupTitles={true} 
          />
          {filterDataError && <p className="text-xs text-destructive mt-1">Failed to load attributes: {filterDataError}</p>}
        </motion.div>

        {/* Filter Badges */}
        <AnimatePresence>
          {activeFilterBadges.length > 0 && (
            <motion.div 
              className="flex flex-col gap-2 pt-3"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div className="flex flex-wrap gap-1">
                <AnimatePresence>
                  {activeFilterBadges.map((badge) => (
                    <motion.div
                      key={badge.id}
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.8 }}
                      transition={{ duration: 0.2 }}
                    >
                      <Badge variant="secondary" className="group relative px-2 py-1 text-xs">
                        {badge.label}
                        <button 
                          onClick={() => handleRemoveBadge(badge)} 
                          className="ml-1 -mr-1 p-0.5 rounded-full opacity-50 group-hover:opacity-100 hover:bg-muted transition-opacity" 
                          aria-label={`Remove ${badge.label} filter`}
                        >
                          <X size={10} />
                        </button>
                      </Badge>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2, delay: 0.1 }}
              >
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={handleResetAllFilters} 
                  className="text-muted-foreground text-xs w-fit px-2 py-1 h-auto mt-1" 
                  aria-label="Reset all filters"
                >
                  <Eraser size={10} className="mr-1" /> Clear All
                </Button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default SodaSearchFilterControls; 